import type { Address as AddressType } from 'shared/types/account/Address';
import Radio from 'components/gruenland/atoms/radio';
import Typography from 'components/gruenland/atoms/typography';
import { TypographyProps } from 'components/gruenland/atoms/typography/types';
import toAccountUrl from 'helpers/account/toAccountUrl';
import { useFormat } from 'helpers/hooks/useFormat';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { tablet } from 'helpers/utils/screensizes';
import { AddressFormData } from './address-form';
import usePropsToAddressType from './mapPropsToAddressType';
import EditCTA from '../../account-atoms/edit-cta';

export interface AddressProps {
  address: AddressType;
  isDefaultAddress?: boolean;
  isChecked: boolean;
  selectAddress: (address: AddressFormData) => void;
}

const Address: React.FC<AddressProps> = ({ address, isDefaultAddress, selectAddress }) => {
  const { mapPropsToAddress } = usePropsToAddressType();
  const { label } = mapPropsToAddress(address as AddressFormData);

  const [isTabletSize] = useMediaQuery(tablet);
  const { formatMessage } = useFormat({ name: 'gruenland' });

  const addressInfoTypographyProps: TypographyProps = {
    className: 'text-secondary-black text-[14px] leading-loose',
  };

  const addressInfoTypographyElements = [
    [address.firstName, address.lastName].filter((x) => !!x).join(' '),
    address.company,
    `${[address.streetName, address.streetNumber].filter((x) => !!x).join(' ')}, ${address.city ?? ''} ${address.postalCode ?? ''} ${address.province ?? ''} - ${address.country ?? ''}`,
    address.additionalAddressInfo,
    [address.fiscalCode, address.vatId].filter((x) => !!x).join(' '),
  ].filter((x) => !!x);

  return (
    <div
      className="flex cursor-pointer items-center justify-between rounded-md border border-neutral-400 p-3 md:px-5 md:py-6 2xl:px-6"
      key={address.addressId}
      onClick={() => isTabletSize && selectAddress(address as AddressFormData)}
    >
      <div className="flex items-center gap-7">
        <Radio
          className="hidden cursor-pointer md:grid"
          inputClassName="cursor-pointer"
          id={address.addressId}
          name={address.addressId}
          value={address.addressId}
          checked={isDefaultAddress}
          onChange={() => selectAddress(address as AddressFormData)}
        />
        <div className="grid gap-6 md:gap-1">
          <div className="flex gap-[5px] md:mb-1">
            <Typography className="text-[14px] font-medium capitalize md:text-[16px]">{label}</Typography>
            {isDefaultAddress && (
              <Typography className="text-[14px] text-secondary-black md:hidden">
                {'- ' + formatMessage({ id: 'common.default', defaultMessage: 'Predefinito' })}
              </Typography>
            )}
          </div>

          <div className="grid">
            {addressInfoTypographyElements.map((element, i) => (
              <Typography key={i} {...addressInfoTypographyProps}>
                {element}
              </Typography>
            ))}
          </div>
        </div>
      </div>

      <div onClick={(e) => e.stopPropagation()}>
        <EditCTA editHref={toAccountUrl(`?hash=addresses&id=address_${address.addressId}`)} />
      </div>
    </div>
  );
};

export default Address;
